import { logEvent, setUserProperties } from 'firebase/analytics';
import ReactDOM from 'react-dom/client';
import '../shared/styles/index.css';
import ThemeScope from '../shared/styles/ThemeScope';
import '../shared/translation/i18n';
import i18n from '../shared/translation/i18n';
import { AccountCreationProvider } from './common/context/AccountCreationContext';
import { AuthProvider } from './common/context/AuthContext';
import WidgetModal from './components/modals/WidgetModal';
import { analytics } from './config/firebaseConfig';
import reportWebVitals from './reportWebVitals';

const getDeviceType = (): string => {
	const ua = navigator.userAgent;
	if (/mobile/i.test(ua)) return 'mobile';
	if (/tablet/i.test(ua) || /ipad/i.test(ua)) return 'tablet';
	return 'desktop';
};

const initializeWidget = () => {
	// if ((window as any).widgetInitialized && process.env.NODE_ENV !== 'development') {
	// 	return;
	// }

	// Default values for development
	let widgetPartnerId = process.env.NODE_ENV === 'development' 
		? process.env.REACT_APP_PARTNER_ID 
		: '';
	let gaId = process.env.NODE_ENV === 'development' 
		? process.env.REACT_APP_GA_ID 
		: '';
	let pageUrl = window.location.href;
	let uiSize = 'large' as 'small' | 'medium' | 'large' | 'monochrome';
	let genderSpecific = 'false' as 'false' | 'm' | 'f';
	let partnerTheme: any = {};

	// In production, wait for initialization data from parent
	if (process.env.NODE_ENV !== 'development') {
		// Set up message listener for initialization data
		const handleInitMessage = (event: MessageEvent) => {
			if (event.data.type === 'WIDGET_INIT') {
				console.debug('[WIDGET] Received init data from button:', event.data.payload);
				const options = event.data.payload;
				
				widgetPartnerId = options.partnerId || '';
				gaId = options.gaId || '';
				pageUrl = options.currentUrl || window.location.href;
				
				// UI Size
				const customUiSize = options.uiSize || 'large';
				const validSizes = ['small', 'medium', 'large', 'monochrome'];
				if (validSizes.includes(customUiSize)) {
					uiSize = customUiSize as 'small' | 'medium' | 'large' | 'monochrome';
				} else {
					console.error('Error : Invalid `uiSize`. The widget will be initialized with the default size.');
					uiSize = 'large';
				}

				// Gender specific
				const customGenderSpecific = options.genderSpecific ? 'm' : 'false';
				const validGenders = ['false', 'm', 'f'];
				if (validGenders.includes(customGenderSpecific)) {
					genderSpecific = customGenderSpecific as 'false' | 'm' | 'f';
				} else {
					console.error('Error : Invalid `genderSpecific`. The widget will be initialized with the default value.');
				}

				// Language
				const customLang = options.lang || 'en';
				i18n.changeLanguage(customLang);

				// TODO: Handle partner theme colors from options if needed
				
				// Remove the listener after initialization
				window.removeEventListener('message', handleInitMessage);
				
				// Render the widget
				renderWidget();
			}
		};

		window.addEventListener('message', handleInitMessage);
		
		// Signal that widget is ready to receive init data
		console.debug('[WIDGET] Sending WIDGET_READY to button');
		window.parent.postMessage({ type: 'WIDGET_READY' }, '*');
		return;
	} else {
		// Development mode - use environment variables and URL params for backward compatibility
		const urlParams = new URLSearchParams(window.location.search);

		if (!widgetPartnerId) {
			console.error('Error : Missing partnerId. The widget will not be initialized.');
			return;
		}

		// UI Size
		let customUiSize = process.env.REACT_APP_UI_SIZE || urlParams.get('data-ui-size') || 'large';
		const validSizes = ['small', 'medium', 'large', 'monochrome'];
		if (validSizes.includes(customUiSize)) {
			uiSize = customUiSize as 'small' | 'medium' | 'large' | 'monochrome';
		} else {
			console.error('Error : Invalid `data-ui-size`. The widget will be initialized with the default size.');
			uiSize = 'large';
		}

		// Gender specific
		let customGenderSpecific = process.env.REACT_APP_GENDER_SPECIFIC || urlParams.get('data-gender') || 'false';
		const validGenders = ['false', 'm', 'f'];
		if (validGenders.includes(customGenderSpecific)) {
			genderSpecific = customGenderSpecific as 'false' | 'm' | 'f';
		} else {
			console.error('Error : Invalid `data-gender`. The widget will be initialized with the default value.');
		}

		// Build theme object from environment variables
		const colorMapping = {
			popsizePrimary90: 'primary90',
			popsizePrimary50: 'primary50',
			popsizePrimary20: 'primary20',
			popsizePrimary10: 'primary10',
			popsizeSecondary90: 'secondary90',
			popsizeSecondary50: 'secondary50',
			popsizeSecondary20: 'secondary20',
			popsizeSecondary10: 'secondary10',
			popsizeNeutral90: 'neutral90',
			popsizeNeutral50: 'neutral50',
			popsizeNeutral20: 'neutral20',
			popsizeNeutral10: 'neutral10',
		};

		// Read environment variables for each color key
		Object.entries(colorMapping).forEach(([dataKey, themeKey]) => {
			const envVarName = `REACT_APP_${dataKey.toUpperCase()}`;
			const customValue = process.env[envVarName] || urlParams.get(`data-${dataKey}`);
			if (customValue) {
				partnerTheme[themeKey] = customValue;
			}
		});

		renderWidget();
	}

	function renderWidget() {
		if (!widgetPartnerId) {
			console.error('Error : Missing partnerId. The widget will not be initialized.');
			return;
		}

		// Initialize Google Analytics
		if (analytics) {
			logEvent(analytics, 'page_view', { page_path: pageUrl });
			setUserProperties(analytics, { language: i18n.language, device_type: getDeviceType() });
		}

		// TODO: it's a bit weird to create it like this: hardcode popsize-widget-container in the iframe.html in the end ;)
		let widgetContainer;
		widgetContainer = document.createElement('div');
		widgetContainer.id = 'popsize-widget-container';
		widgetContainer.className = 'popsize-widget-container';
		document.body.appendChild(widgetContainer);

		const root = ReactDOM.createRoot(widgetContainer);
		root.render(
			<AuthProvider>
				<AccountCreationProvider>
					<ThemeScope theme={Object.keys(partnerTheme).length > 0 ? partnerTheme : undefined}>
					<WidgetModal 
						partnerId={widgetPartnerId} 
						uiSize={uiSize} 
						genderSpecific={genderSpecific}
						partnerTheme={Object.keys(partnerTheme).length > 0 ? partnerTheme : undefined}
						currentUrl={pageUrl}
					/>
					</ThemeScope>
				</AccountCreationProvider>
			</AuthProvider>
		);

		(window as any).widgetInitialized = true;
	}
};

initializeWidget();
reportWebVitals();
